import type { Trip, TripParticipant, User } from '../types';

/**
 * Everyone travelling, in one place.
 *
 * The owner used to be absent from `trip.participants` (membership was
 * `ownerId` OR a participant row), so every screen re-derived the full set as
 * `[owner, ...participants]` and every count as `participants.length + 1`.
 * That fiction was spread across a dozen files in this repo alone and drifted:
 * some sites deduped, some didn't, and the per-person expense average divided
 * by the wrong number. Since the server backfilled the owner's row
 * (migration 20260811090000) the participant list is the whole list — these
 * helpers exist so that stays true in exactly one place.
 */

type WithParticipants = Pick<Trip, 'ownerId'> & { participants?: TripParticipant[] };

export function tripMembers(trip: WithParticipants): TripParticipant[] {
  return trip.participants ?? [];
}

export function tripMemberCount(trip: WithParticipants): number {
  return tripMembers(trip).length;
}

export function tripMemberUsers(trip: WithParticipants): TripParticipant['user'][] {
  return tripMembers(trip).map(p => p.user);
}

export function tripMemberIds(trip: WithParticipants): string[] {
  return tripMembers(trip).map(p => p.userId);
}

/**
 * Members who can open the trip. A traveller without an account (ADR-0021)
 * has no suitcase and reads nothing, so they don't make a trip a group one.
 */
export function tripAccountMembers(trip: WithParticipants): TripParticipant[] {
  return tripMembers(trip).filter(p => !p.user.isPlaceholder);
}

/** Members other than the owner — for UI that renders the owner separately. */
export function tripCoParticipants(trip: WithParticipants): TripParticipant[] {
  return tripMembers(trip).filter(p => p.userId !== trip.ownerId);
}

export type TripMemberUser = Pick<User, 'id' | 'username' | 'displayName' | 'avatarUrl'>;
